import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register } from '../services/auth';
import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';

const ROLE_LABELS: Record<'CUSTOMER' | 'OPERATOR' | 'GUIDE', string> = {
  CUSTOMER: 'Traveler & Supporter',
  OPERATOR: 'Tour Operator Partner',
  GUIDE: 'Community Guide',
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'role' | 'details'>('role');
  const [role, setRole] = useState<'CUSTOMER' | 'OPERATOR' | 'GUIDE' | null>(null);
  const [name, setName] = useState('Demo User');
  const [email, setEmail] = useState('demo@example.com');
  const [password, setPassword] = useState('password123');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const backendRole = role === 'OPERATOR' ? 'operator' : role === 'GUIDE' ? 'guide' : 'user';
      const user = await register(name, email, password, backendRole);
      
      // If OPERATOR or GUIDE, redirect to verification flow
      if (role === 'OPERATOR' || role === 'GUIDE') {
        navigate('/partner/apply', { replace: true });
      } else {
        // CUSTOMER redirects to home
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page login-page">
      <PageHeader
        kicker="Join SafariCo Hub"
        title="Create Your Account"
        subtitle="Choose how you'll take part in our East African safari association."
      />

      {step === 'role' ? (
        <div style={{ marginTop: 32, display: 'grid', gap: 18, gridTemplateColumns: 'repeat(3, 1fr)', maxWidth: 1000, margin: '32px auto' }}>
          <Card
            title="Traveler & Supporter"
            style={{
              cursor: 'pointer',
              border: role === 'CUSTOMER' ? '2px solid var(--primary, #007bff)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
            onClick={() => {
              setRole('CUSTOMER');
              setStep('details');
            }}
          >
            <div className="kicker">Travel with purpose</div>
            <p className="muted" style={{ marginTop: 12 }}>
              Browse community-vetted safaris, join open groups, and manage your bookings. Every
              trip you take funds conservation and local development.
            </p>
            <button className="btn btnPrimary" style={{ width: '100%', marginTop: 16 }}>
              Choose
            </button>
          </Card>

          <Card
            title="Tour Operator Partner"
            style={{
              cursor: 'pointer',
              border: role === 'OPERATOR' ? '2px solid var(--primary, #007bff)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
            onClick={() => {
              setRole('OPERATOR');
              setStep('details');
            }}
          >
            <div className="kicker">Run community-benefit operations</div>
            <p className="muted" style={{ marginTop: 12 }}>
              Register your company as a vetted partner, connect with our travel agency and donor
              network, and report transparently on the impact you create.
            </p>
            <button className="btn btnPrimary" style={{ width: '100%', marginTop: 16 }}>
              Choose
            </button>
          </Card>

          <Card
            title="Community Guide"
            style={{
              cursor: 'pointer',
              border: role === 'GUIDE' ? '2px solid var(--primary, #007bff)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
            onClick={() => {
              setRole('GUIDE');
              setStep('details');
            }}
          >
            <div className="kicker">Lead safari experiences</div>
            <p className="muted" style={{ marginTop: 12 }}>
              Get verified, access training and fair-work protections, and lead travelers through
              the communities and landscapes you know best.
            </p>
            <button className="btn btnPrimary" style={{ width: '100%', marginTop: 16 }}>
              Choose
            </button>
          </Card>
        </div>
      ) : (
        <div style={{ maxWidth: 400, margin: '32px auto' }}>
          <form onSubmit={handleSubmit}>
            <label>
              Full Name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>

            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>

            <p className="muted" style={{ fontSize: '0.9em', marginTop: 12 }}>
              Registering as: <strong>{role ? ROLE_LABELS[role] : ''}</strong>
            </p>

            <button type="submit" disabled={loading} className="btn btnPrimary" style={{ width: '100%', marginTop: 12 }}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
            {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

            <button
              type="button"
              onClick={() => {
                setStep('role');
                setError(null);
              }}
              className="btn btnSecondary"
              style={{ width: '100%', marginTop: 8 }}
            >
              Back
            </button>
          </form>
        </div>
      )}

      <p className="muted" style={{ marginTop: 18, textAlign: 'center' }}>
        Already have an account? <a href="/login">Log in here</a>
      </p>
    </div>
  );
}
